import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { Container } from 'reactstrap';

import AppBreadcrumb from 'components/App/Breadcrumb';
import Loading from 'components/App/Loading';
import AppMeta from 'components/App/Meta';
import AppSelectValidation from 'components/App/SelectValidation';
import CalendarComponent from 'components/DashboardPage/PriceAndRoomState/Calendar';
import FlatPickInput from 'components/App/FlatPickInput';

import { LOCATIONS, ROOM_TYPE } from 'constants/index';

import styles from './Calendar.module.scss';

const today = new Date();
const endDay = new Date(today.setDate(today.getDate() + 30));

const breadcrumbList = [
  {
    name: 'Giá và tình trạng phòng trống',
    isActive: false,
    link: LOCATIONS.LOCATION_LIST,
  },
  {
    name: 'Lịch',
    isActive: true,
    link: '',
  },
];

function Calendar(props) {
  const {
    roomStatusList,
    loadingRoomStatusList,
    actionGetRoomStatusList,
  } = props;

  const [dayStart, setDayStart] = React.useState(new Date());
  const [dayEnd, setDayEnd] = React.useState(endDay);
  const [roomId, setRoomId] = React.useState('');
  const [isHasFilter, setIsHasFilter] = React.useState(false);
  const [newRoomStatusList, setNewRoomStatusList] = React.useState([]);

  const location = useLocation();

  const query = new URLSearchParams(location.search).get('place');

  const dayStartFormat = React.useMemo(() => moment(dayStart).format('YYYY-MM-DD'), [dayStart]);
  const dayEndFormat = React.useMemo(() => moment(dayEnd).format('YYYY-MM-DD'), [dayEnd]);

  const optionFilter = React.useMemo(() => {
    const allRoomsOption = { label: 'Tất cả phòng', value: '' };
    const roomOptions = roomStatusList.map((room) => ({
      label: room.name || ROOM_TYPE[room.roomName]?.label,
      value: room._id,
    }));
    return [allRoomsOption, ...roomOptions];
  }, [roomStatusList]);

  const changeRoomId = React.useCallback((e) => {
    setRoomId(e.value);
    if (e.value !== '') {
      setNewRoomStatusList(roomStatusList.filter((room) => room._id === e.value));
    } else {
      setNewRoomStatusList(roomStatusList);
    }
  }, [roomStatusList]);

  const handleAddEndDate = React.useCallback(() => {
    setDayEnd((prev) => {
      const newEndDate = new Date(prev.setDate(today.getDate() + 30));
      return newEndDate;
    });
  }, []);

  React.useEffect(() => {
    if (JSON.stringify(dayEnd) !== '{}') {
      actionGetRoomStatusList({
        place: query,
        start: dayStartFormat,
        end: dayEndFormat,
      });
    }
  }, [actionGetRoomStatusList, dayStartFormat, dayEndFormat, query, dayEnd, roomId]);

  const onChangeStartDate = React.useCallback((e) => {
    switch (e.length) {
      case 1:
        setDayStart(e[0]);
        setIsHasFilter(true);
        break;
      case 2:
        setDayStart(e[0]);
        setDayEnd(e[1]);
        setIsHasFilter(true);
        break;

      default:
        setDayStart(new Date());
        setDayEnd(endDay);
        setIsHasFilter(false);
        break;
    }
  }, []);

  return (
    <div className={`page-content ${styles.Calendar}`}>
      <AppMeta
        name="Lịch"
      />

      <Container fluid>
        <AppBreadcrumb
          pageTitle="LỊCH"
          breadcrumbList={breadcrumbList}
        />

        <Loading loading={loadingRoomStatusList} loaderOnPage />

        <div className="FilterBar">
          <div className="FilterBar__Input">
            <span>Tìm theo Phòng</span>

            <AppSelectValidation
              placeholder="Tất cả phòng"
              name="roomNameRequired"
              key="roomNameRequired"
              options={optionFilter}
              value={roomId}
              onChange={changeRoomId}
            />
          </div>

          <div className="FilterBar__Input">
            <span>Chọn thời gian xem</span>

            <FlatPickInput
              name="startDate"
              onChange={onChangeStartDate}
              options={{
                mode: 'range',
                dateFormat: 'd/m/Y',
                minDate: new Date(),
              }}
            />
          </div>
        </div>

        <CalendarComponent
          dayStart={dayStart}
          dayEnd={dayEnd}
          roomStatusList={newRoomStatusList.length > 0 ? newRoomStatusList : roomStatusList}
          isHasFilter={isHasFilter}
          handleAddEndDate={handleAddEndDate}
          place={query}
          dayStartFormat={dayStartFormat}
          dayEndFormat={dayEndFormat}
        />
      </Container>
    </div>
  );
}

Calendar.propTypes = {
  roomStatusList: PropTypes.instanceOf(Array),
  loadingRoomStatusList: PropTypes.bool.isRequired,
  actionGetRoomStatusList: PropTypes.func.isRequired,
};

Calendar.defaultProps = {
  roomStatusList: [],
};

export default Calendar;
